/** QuickHouse — Schémas zod du modèle de données (source de vérité serveur / outils / client) */
import { z } from 'zod';
import { openingCovered } from './plan3d.js';

// ── Identifiants ──────────────────────────────────────────────
export function newId(prefix) {
  return `${prefix}_${crypto.randomUUID().slice(0, 8)}`;
}

// ── Schémas de base ───────────────────────────────────────────
export const PointSchema = z.object({
  x: z.number(),
  y: z.number(),
});

export const WallSchema = z.object({
  id: z.string().min(1),
  a: PointSchema,
  b: PointSchema,
  thickness: z.number().positive(),
  height: z.number().positive().nullable().default(null),  // hauteur à l'extrémité a (null → étage)
  heightB: z.number().positive().nullable().default(null), // hauteur à l'extrémité b (null → comme a) : mur rampant
  finishOut: z.string().nullable().default(null),          // finition face EXTÉRIEURE ('plaster', 'paint-white', 'tex:<id>'…)
  finishIn: z.string().nullable().default(null),           // finition face INTÉRIEURE
  folderId: z.string().nullable().default(null),           // dossier de l'arborescence
  parentId: z.string().nullable().default(null),           // élément parent (hiérarchie : bouger le parent bouge l'enfant)
  hidden: z.boolean().default(false),                      // masqué en 2D/3D
});

/** Hauteurs effectives d'un mur aux extrémités a et b */
export function wallHeights(wall, floorHeight) {
  const hA = wall.height ?? floorHeight;
  return { hA, hB: wall.heightB ?? hA };
}

export const OPENING_TYPES = ['door', 'window', 'garage', 'passage'];

/** Variantes par type d'ouverture (libellés français dans l'UI) */
export const OPENING_VARIANTS = {
  door: ['single', 'double', 'doubleGlazed', 'sliding'],
  window: ['single', 'double', 'bay', 'pocket', 'french', 'frenchDouble', 'velux'],
  garage: ['roller', 'double', 'sliding'],
  passage: ['plain'], // ouverture libre, sans menuiserie
};

/** Dimensions par défaut { width, height, sillHeight } par type/variante */
export const OPENING_DEFAULTS = {
  door: { single: [90, 215, 0], double: [140, 215, 0], doubleGlazed: [140, 215, 0], sliding: [90, 215, 0] },
  window: {
    single: [60, 125, 100], double: [120, 125, 100],
    bay: [240, 215, 0], pocket: [240, 215, 0],
    french: [90, 215, 0], frenchDouble: [160, 215, 0],
    velux: [114, 118, 0], // height = profondeur sur le plan (toit)
  },
  garage: { roller: [240, 200, 0], double: [240, 200, 0], sliding: [300, 200, 0] },
  passage: { plain: [120, 205, 0] },
};

export function openingDefaults(type, variant) {
  const [width, height, sillHeight] = OPENING_DEFAULTS[type]?.[variant]
    ?? OPENING_DEFAULTS[type]?.single ?? [90, 215, 0];
  return { width, height, sillHeight };
}

export const OpeningSchema = z.object({
  id: z.string().min(1),
  type: z.enum(OPENING_TYPES),
  variant: z.string().default('single'),
  wallId: z.string().nullable().default(null),      // null uniquement pour un velux (toit)
  position: PointSchema.optional(),                  // velux : position sur le plan
  offset: z.number().default(0),                     // distance a → CENTRE (peut déborder sur un mur colinéaire)
  width: z.number().positive(),
  height: z.number().positive(),
  sillHeight: z.number().nonnegative().default(0), // allège / hauteur basse (0 pour une porte)
  arch: z.number().default(0), // linteau arrondi : flèche en cm (+ convexe vers le haut, − concave)
  frameMaterial: z.enum(['pvc', 'wood', 'alu']).default('pvc'), // matériau de la menuiserie
  open: z.boolean().default(false),                             // vantaux ouverts en 3D
  shutter: z.enum(['none', 'roller', 'swing']).default('none'), // volet : aucun / roulant / battants
  shutterStyle: z.enum(['solid', 'louvered']).default('solid'), // battants pleins ou ajourés (persiennes)
  shutterClosed: z.boolean().default(false),                    // volet fermé (couvre l'ouverture)
  shutterColor: z.string().default('wood'),                     // peinture texturée bois : wood/white/grey/blue/green/red/brown
  meta: z.record(z.string(), z.any()).default({}),
  folderId: z.string().nullable().default(null),
  parentId: z.string().nullable().default(null),
  hidden: z.boolean().default(false),
});

// ── Escaliers ─────────────────────────────────────────────────
// straight (droit ou à angles : le chemin peut tourner) / ladder (échelle de meunier) :
// axe = polyligne `path`, largeur `width`. spiral (colimaçon) : centre + rayon + balayage.
export const StairSchema = z.union([
  z.object({
    id: z.string().min(1),
    kind: z.enum(['straight', 'ladder']),
    path: z.array(PointSchema).min(2),
    width: z.number().positive().default(90),
    cutSlab: z.boolean().default(true), // trémie : découpe la dalle du niveau supérieur
    folderId: z.string().nullable().default(null),
    parentId: z.string().nullable().default(null),
    hidden: z.boolean().default(false),
  }),
  z.object({
    id: z.string().min(1),
    kind: z.literal('spiral'),
    center: PointSchema,
    radius: z.number().positive().default(80),
    startAngle: z.number().default(0),   // degrés
    sweep: z.number().default(360),      // degrés (signe = sens)
    cutSlab: z.boolean().default(true),
    folderId: z.string().nullable().default(null),
    parentId: z.string().nullable().default(null),
    hidden: z.boolean().default(false),
  }),
]);

// ── Pièces : zones de sol composées de blocs ─────────────────
// rect : rectangle avec rayon d'angle optionnel.
// poly : polygone dont chaque côté peut être bombé (bulge = 2·flèche/corde, convention DXF).
export const RoomBlockSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('rect'),
    x: z.number(),
    y: z.number(),
    w: z.number().positive(),
    h: z.number().positive(),
    radius: z.number().nonnegative().default(0),
  }),
  z.object({
    type: z.literal('poly'),
    points: z.array(z.object({
      x: z.number(),
      y: z.number(),
      bulge: z.number().default(0),
    })).min(3),
  }),
]);

export const SLAB_MATERIALS = ['concrete', 'wood', 'steel', 'other'];
export const ROOF_MATERIALS = ['slate', 'tile', 'flat', 'green']; // ardoise, tuile, plat, végétalisé

export const RoomLabelSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  // kind 'slab' : DALLE (plancher) — épaisseur/matériau/élévation. elevation > 0 = MEZZANINE.
  // kind 'roof' : TOIT — matériau de couverture, pente (°) et direction de descente ;
  //               elevation = hauteur de la base du toit (défaut : hauteur de l'étage).
  kind: z.enum(['room', 'slab', 'roof']).default('room'),
  position: PointSchema,              // ancre de l'étiquette
  blocks: z.array(RoomBlockSchema).default([]), // vide = simple étiquette (rétro-compatible)
  thickness: z.number().positive().nullable().default(null), // dalle : épaisseur (null → réglage projet)
  material: z.enum([...SLAB_MATERIALS, ...ROOF_MATERIALS]).default('concrete'),
  elevation: z.number().nonnegative().default(0),
  slope: z.number().nonnegative().default(0),                // toit : pente en degrés (0 = plat)
  slopeDir: z.enum(['N', 'S', 'E', 'W']).default('S'),       // toit : direction de la DESCENTE
  finish: z.string().nullable().default(null),               // revêtement 3D : pièce = sol ('tile-floor', 'parquet', 'tex:<id>'…), toit/dalle = surcharge de texture
  folderId: z.string().nullable().default(null),
  parentId: z.string().nullable().default(null),
  hidden: z.boolean().default(false),
});

// ── Charpente : poutres, linteaux, poteaux (pergolas, tonnelles…) ──
export const BEAM_MATERIALS = ['wood', 'wood-dark', 'steel', 'white']; // chêne, bois foncé, acier anthracite, laqué blanc

export const BeamSchema = z.discriminatedUnion('kind', [
  // Poutre horizontale (ou rampante) : axe a→b, section w (large) × h (haut),
  // elevA = hauteur du DESSOUS côté a ; elevB null → même hauteur (sinon rampante : chevron)
  z.object({
    kind: z.literal('beam'),
    id: z.string().min(1),
    a: PointSchema,
    b: PointSchema,
    w: z.number().positive().default(15),
    h: z.number().positive().default(20),
    elevA: z.number().nullable().default(null),  // null → sous plafond (hauteur étage − h)
    elevB: z.number().nullable().default(null),  // null → comme elevA
    material: z.enum(BEAM_MATERIALS).default('wood'),
    folderId: z.string().nullable().default(null),
    parentId: z.string().nullable().default(null),
    hidden: z.boolean().default(false),
  }),
  // Poteau vertical : position p, section w × d, de elevation à elevation + height
  z.object({
    kind: z.literal('post'),
    id: z.string().min(1),
    p: PointSchema,
    w: z.number().positive().default(15),
    d: z.number().positive().nullable().default(null), // null → carré (= w)
    height: z.number().positive().nullable().default(null), // null → hauteur de l'étage
    elevation: z.number().default(0),
    material: z.enum(BEAM_MATERIALS).default('wood'),
    folderId: z.string().nullable().default(null),
    parentId: z.string().nullable().default(null),
    hidden: z.boolean().default(false),
  }),
]);

// ── Cotes manuelles (mesures posées sur le plan 2D) ──────────
export const DimSchema = z.object({
  id: z.string().min(1),
  a: PointSchema,
  b: PointSchema,
  folderId: z.string().nullable().default(null),
  parentId: z.string().nullable().default(null),
  hidden: z.boolean().default(false),
});

// ── Objets de la bibliothèque posés sur le plan ───────────────
export const PlacedItemSchema = z.object({
  id: z.string().min(1),
  catalogId: z.string().min(1),                   // référence au catalogue (intégré ou personnalisé)
  position: PointSchema,                          // centre de l'empreinte (cm)
  rotation: z.number().default(0),                // degrés, sens horaire plan
  elevation: z.number().nullable().default(null), // hauteur de pose (null → défaut catalogue)
  scale: z.number().positive().default(1),
  label: z.string().nullable().default(null),     // libellé optionnel (ex. « PC cuisine »)
  folderId: z.string().nullable().default(null),
  parentId: z.string().nullable().default(null),
  hidden: z.boolean().default(false),
});

/** Dossier de l'arborescence de contenu (par étage) */
export const FolderSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  parentId: z.string().nullable().default(null), // sous-dossiers possibles
});

// ── Étage + invariants ────────────────────────────────────────
function wallLen(w) {
  return Math.hypot(w.b.x - w.a.x, w.b.y - w.a.y);
}

export const FloorSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  level: z.number().int(),
  height: z.number().positive(),
  walls: z.array(WallSchema).default([]),
  openings: z.array(OpeningSchema).default([]),
  rooms: z.array(RoomLabelSchema).default([]),
  stairs: z.array(StairSchema).default([]),
  items: z.array(PlacedItemSchema).default([]),
  beams: z.array(BeamSchema).default([]),
  dims: z.array(DimSchema).default([]),
  folders: z.array(FolderSchema).default([]),
}).superRefine((floor, ctx) => {
  const wallsById = new Map(floor.walls.map((w) => [w.id, w]));
  const byWall = new Map();
  for (const [i, op] of floor.openings.entries()) {
    if (op.wallId === null) {
      // Ouverture de toit (velux) : pas de mur, mais une position obligatoire
      if (!(op.type === 'window' && op.variant === 'velux')) {
        ctx.addIssue({ code: 'custom', path: ['openings', i], message: `Seul un velux peut être sans mur (${op.id})` });
      } else if (!op.position) {
        ctx.addIssue({ code: 'custom', path: ['openings', i], message: `Velux ${op.id} sans position` });
      }
      continue;
    }
    const wall = wallsById.get(op.wallId);
    if (!wall) {
      ctx.addIssue({ code: 'custom', path: ['openings', i], message: `Mur introuvable : ${op.wallId}` });
      continue;
    }
    const len = wallLen(wall);
    // L'ouverture peut déborder sur un mur COLINÉAIRE joint (fenêtre à cheval sur deux murs)
    if ((op.offset - op.width / 2 < -0.01 || op.offset + op.width / 2 > len + 0.01)
      && !openingCovered(op, wall, floor.walls)) {
      ctx.addIssue({
        code: 'custom', path: ['openings', i],
        message: `Ouverture ${op.id} hors du mur ${op.wallId} (longueur ${Math.round(len)} cm, centre à ${op.offset}, largeur ${op.width}) et non couverte par un mur aligné`,
      });
    }
    // Hauteur du mur à l'aplomb du centre de l'ouverture (mur rampant : interpolation)
    const { hA, hB } = wallHeights(wall, floor.height);
    const hMax = len > 0 ? hA + ((hB - hA) * op.offset) / len : hA;
    if (op.sillHeight + op.height > hMax + 0.01) {
      ctx.addIssue({
        code: 'custom', path: ['openings', i],
        message: `Ouverture ${op.id} trop haute : allège ${op.sillHeight} + hauteur ${op.height} > ${Math.round(hMax)} cm`,
      });
    }
    if (!byWall.has(op.wallId)) byWall.set(op.wallId, []);
    byWall.get(op.wallId).push(op);
  }
  // Chevauchements sur un même mur
  for (const ops of byWall.values()) {
    const sorted = [...ops].sort((p, q) => p.offset - q.offset);
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const cur = sorted[i];
      if (cur.offset - cur.width / 2 < prev.offset + prev.width / 2 - 0.01) {
        ctx.addIssue({
          code: 'custom', path: ['openings'],
          message: `Ouvertures ${prev.id} et ${cur.id} se chevauchent sur le mur ${cur.wallId}`,
        });
      }
    }
  }
});

// ── Projet ────────────────────────────────────────────────────
export const SettingsSchema = z.object({
  units: z.literal('cm').default('cm'),
  defaultWallHeight: z.number().positive().default(250),
  defaultWallThickness: z.number().positive().default(20),
  slabThickness: z.number().positive().default(20),
  // Azimut du nord géographique dans le plan : degrés depuis le haut du plan (−Y), sens horaire
  north: z.number().default(0),
  grid: z.object({
    size: z.number().positive().default(10),
    snap: z.boolean().default(true),
  }).default({ size: 10, snap: true }),
});

export const ProjectSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string().min(1),
  name: z.string().min(1),
  createdAt: z.string(),
  updatedAt: z.string(),
  // ── Propriété / partage (suite Quick-Apps) ──
  // owner = username du portail propriétaire ; null = projet hérité (aucun
  // propriétaire, visible de tous) le temps de la migration. sharedWith = comptes
  // avec qui le projet est partagé. Ces champs ne sont JAMAIS modifiables par un
  // PUT de projet côté client : le serveur les rétablit depuis le fichier (voir
  // routes/projects.js) et le partage passe par la route dédiée /:id/share.
  owner: z.string().nullable().default(null),
  sharedWith: z.array(z.string()).default([]),
  settings: SettingsSchema,
  floors: z.array(FloorSchema).min(1),
});

// ── Fabriques ─────────────────────────────────────────────────
export function createEmptyFloor(name = 'RDC', level = 0, height = 250) {
  return { id: newId('f'), name, level, height, walls: [], openings: [], rooms: [], stairs: [], items: [], beams: [], dims: [], folders: [] };
}

export function createEmptyProject(name) {
  const now = new Date().toISOString();
  return {
    schemaVersion: 1,
    id: newId('p'),
    name,
    createdAt: now,
    updatedAt: now,
    settings: SettingsSchema.parse({}),
    floors: [createEmptyFloor()],
  };
}

/** Valide un projet ; retourne { ok, project?, errors? } */
export function validateProject(json) {
  const res = ProjectSchema.safeParse(json);
  if (res.success) return { ok: true, project: res.data };
  const errors = res.error.issues.map((i) => `${i.path.join('.')} : ${i.message}`);
  return { ok: false, errors };
}
